import { Brandmark } from "#src/components/Presentational/Logos/Brandmark";
import config from "#src/config";
import {
  Button,
  EmptyState,
  Icon,
  IconLoading,
  LoadingState,
  Popover,
  RichTextDataDisplay,
  RichTextMarkdownDataDisplay,
  type PopoverProps,
} from "@validereinc/common-components";
import classNames from "classnames/bind";
import React, { type ReactNode } from "react";
import styles from "./AssistantPopover.module.css";

const cx = classNames.bind(styles);

export const AssistantPopover = ({
  children,
  content,
  contentFooter,
  isContentRevealed,
  setIsContentRevealed,
  error,
  errorAction,
  onClose,
  onGoBack,
  isLoading,
  loadingStateLabel,
  isBusy,
  isStaticContentShown,
  isSeen,
  ...restProps
}: PopoverProps & {
  content?: ReactNode;
  contentFooter?: ReactNode;
  isContentRevealed?: boolean;
  setIsContentRevealed: (state: boolean) => void;
  error?: unknown;
  errorSuggestion: string;
  errorAction: ReactNode;
  onClose?: () => void;
  isLoading?: boolean;
  loadingStateLabel?: string;
  isBusy?: boolean;
  isSeen?: boolean;
  isStaticContentShown?: boolean;
  onGoBack?: () => void;
}) => {
  const emptyContent = (
    <EmptyState
      icon={<Icon variant="empty" />}
      variant="default"
      title="I have nothing to say right now"
      suggestion="Interact with parts of the page with my icon to get started or search the knowledge base to find what you need."
      className={cx("empty-state-container")}
      action={
        <Button
          variant="outline"
          size="small"
          onClick={() => {
            window.open(config.KNOWLEDGE_BASE_URL, "_blank");
          }}
        >
          Open Knowledge Base
        </Button>
      }
    />
  );

  return (
    <Popover
      {...restProps}
      contentClassName={cx("container")}
      contentRender={() => (
        <div className={cx("content-container")}>
          <div className={cx("header")}>
            <div className={cx("header-title-container")}>
              <Icon
                variant="sparkle"
                colour="white"
                weight="fill"
              />
              <p className={cx("header-title")}>Validere Assistant</p>
              {isBusy ? <IconLoading speed="fast" /> : null}
            </div>
            <div className={cx("header-actions-container")}>
              {isStaticContentShown || isLoading ? null : (
                <Button
                  variant="outline"
                  size="xx-small"
                  slotLeft={<Icon variant="arrow-u-up-left" />}
                  onClick={onGoBack}
                >
                  Back
                </Button>
              )}
              <Button
                variant="ghost"
                icon="x"
                iconProps={{
                  size: 24,
                  colour: "white",
                }}
                style={{
                  padding: 0,
                }}
                size="xx-small"
                onClick={onClose}
              />
            </div>
          </div>
          <div className={cx("main")}>
            {!error ? (
              isLoading ? (
                <LoadingState
                  size="small"
                  label={loadingStateLabel ?? ""}
                  indicator={<Brandmark isAnimated />}
                />
              ) : content ? (
                <div className={cx("content")}>
                  {typeof content === "string" ? (
                    <RichTextMarkdownDataDisplay
                      value={content}
                      isInline={false}
                      emptyContent={emptyContent}
                      isLoading={isLoading}
                      isBusy={isBusy}
                      sequentialReveal={!isSeen && !isContentRevealed}
                      isContainerSizing
                      onSequentialRevealComplete={() =>
                        setIsContentRevealed(true)
                      }
                    />
                  ) : (
                    <RichTextDataDisplay
                      value={content}
                      isInline={false}
                      emptyContent={emptyContent}
                      isLoading={isLoading}
                      isBusy={isBusy}
                      sequentialReveal={!isSeen}
                      isContainerSizing
                    />
                  )}
                  {contentFooter && isContentRevealed ? (
                    <RichTextDataDisplay
                      value={contentFooter}
                      isInline={false}
                      isLoading={isLoading}
                      isBusy={isBusy}
                      sequentialReveal={!isSeen}
                      isContainerSizing
                      className={cx("content-footer")}
                    />
                  ) : null}
                </div>
              ) : (
                emptyContent
              )
            ) : (
              <EmptyState
                icon={<Icon variant="smiley-sad" />}
                variant="error"
                title="I'm having some trouble getting what you need"
                suggestion="Refresh me to try again."
                className={cx("empty-state-container")}
                action={errorAction}
              />
            )}
          </div>
        </div>
      )}
    >
      {children}
    </Popover>
  );
};
